/* ═══════════════════════════════════════════════════════════════════════════
   L'onboarding — cinq temps, aucune phrase (PLAN.md §8, J14).

   « Le premier run EST le tutoriel. » Il n'y a pas de niveau d'entraînement à
   part, pas de texte à lire : une simple COUCHE de coaching se pose sur la
   première partie de campagne et surligne, à cinq moments, ce qu'il faut avoir
   compris — bouger, la hitbox ponctuelle, le graze, la bombe. La cinquième
   étape est l'effacement : le jeu continue, seul.

   Ce module ne contient QUE la logique — la machine à cinq temps — et rien du
   DOM : il s'éprouve sans navigateur, en lui donnant des observations
   fabriquées. Le rendu des glyphes vit dans `ui/coach.ts`.

   Deux principes de conception :

   1. Chaque temps AVANCE sur un accomplissement du joueur (il a bougé, il a
      grazé, il a lâché une bombe) OU sur un PLAFOND de temps. Le plafond n'est
      pas un détail : sans lui, un joueur qui refuse de bomber resterait bloqué
      sur l'écran de coaching pour toujours. Un tutoriel qui peut coincer le
      joueur est pire que pas de tutoriel.

   2. Le coach n'écrit RIEN dans la simulation. Il observe, il affiche. La
      partie qui tourne dessous est une vraie partie déterministe — enlever le
      coach ne changerait pas un seul tick.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Les cinq temps de l'apprentissage. `Done` est l'état terminal : le coach
 *  s'efface et ne reparaît plus de la partie. */
export const Beat = {
  /** Bouge — glyphe de directions, jusqu'à ce que le vaisseau se déplace. */
  Move: 0,
  /** Seul CE point te tue — un réticule se resserre sur le cœur du vaisseau. */
  Hitbox: 1,
  /** Frôler récompense — étincelles quand le compteur de graze monte. */
  Graze: 2,
  /** La bombe efface le danger — glyphe d'éclat, jusqu'au premier déclenchement. */
  Bomb: 3,
  /** Fini : le coach a disparu. */
  Done: 4,
} as const;

export type BeatId = (typeof Beat)[keyof typeof Beat];

/** Ce que le coach relève du monde à chaque frame. Volontairement RÉDUIT à des
 *  scalaires, sans référence au `World` : c'est ce qui rend la machine testable
 *  sans faire tourner une simulation. */
export interface OnboardingObs {
  /** Position du vaisseau, en unités monde. Le coach en dérive la distance
   *  parcourue pour savoir si le joueur a « compris qu'on bouge ». */
  playerX: number;
  playerY: number;
  /** Compteurs monotones du monde. */
  grazeCount: number;
  bombsUsed: number;
  /** Projectiles ennemis à l'écran — sert à n'enchaîner sur la hitbox que
   *  lorsqu'il y a une menace à relier au réticule. */
  bulletCount: number;
}

/* Plafonds de durée par temps (secondes) et seuil de déplacement (unités monde).
   Mesurés pour laisser le temps de comprendre sans jamais s'éterniser. */
const MOVE_MAX = 8;
const MOVE_DIST = 60;
const HITBOX_MIN = 3;
const HITBOX_MAX = 8;
const GRAZE_MAX = 12;
const BOMB_MAX = 14;

export class Onboarding {
  private b: BeatId = Beat.Move;
  private t = 0;
  private moved = 0;
  private hasPrev = false;
  private prevX = 0;
  private prevY = 0;
  private baseGraze = 0;
  private baseBombs = 0;

  /** Temps courant. */
  get beat(): BeatId {
    return this.b;
  }

  /** Vrai une fois le tutoriel terminé — le coach doit alors se retirer. */
  get done(): boolean {
    return this.b === Beat.Done;
  }

  /** Fraction écoulée du temps courant, [0,1]. Sert à animer une pastille de
   *  progression discrète, pour signaler que ça avance même si le joueur
   *  n'accomplit pas encore le geste attendu. */
  get progress(): number {
    const max =
      this.b === Beat.Move
        ? MOVE_MAX
        : this.b === Beat.Hitbox
          ? HITBOX_MAX
          : this.b === Beat.Graze
            ? GRAZE_MAX
            : BOMB_MAX;
    const p = this.t / max;
    return p < 0 ? 0 : p > 1 ? 1 : p;
  }

  update(obs: OnboardingObs, dtSec: number): void {
    if (this.b === Beat.Done) return;

    // Distance parcourue, cumulée entre frames. La première frame ne compte pas
    // (aucun « précédent » de référence) — sinon un saut initial fausserait le
    // seuil de déplacement.
    if (this.hasPrev) {
      const dx = obs.playerX - this.prevX;
      const dy = obs.playerY - this.prevY;
      this.moved += Math.sqrt(dx * dx + dy * dy);
    }
    this.prevX = obs.playerX;
    this.prevY = obs.playerY;
    this.hasPrev = true;

    this.t += dtSec;

    switch (this.b) {
      case Beat.Move:
        if (this.moved >= MOVE_DIST || this.t >= MOVE_MAX) this.to(Beat.Hitbox, obs);
        break;
      case Beat.Hitbox:
        // On laisse le réticule un minimum, puis on enchaîne dès qu'il y a des
        // balles (le joueur peut relier « ce point » à « ces menaces »), sinon
        // au plafond.
        if ((this.t >= HITBOX_MIN && obs.bulletCount > 0) || this.t >= HITBOX_MAX)
          this.to(Beat.Graze, obs);
        break;
      case Beat.Graze:
        if (obs.grazeCount > this.baseGraze || this.t >= GRAZE_MAX) this.to(Beat.Bomb, obs);
        break;
      case Beat.Bomb:
        if (obs.bombsUsed > this.baseBombs || this.t >= BOMB_MAX) this.to(Beat.Done, obs);
        break;
    }
  }

  private to(next: BeatId, obs: OnboardingObs): void {
    this.b = next;
    this.t = 0;
    // On rebase les compteurs à l'ENTRÉE du temps concerné : chaque leçon attend
    // un geste FRAIS accompli pendant qu'elle est affichée, pas un graze survenu
    // par hasard trois temps plus tôt.
    if (next === Beat.Graze) this.baseGraze = obs.grazeCount;
    if (next === Beat.Bomb) this.baseBombs = obs.bombsUsed;
  }
}
